/**
 * QR Code Generator Editor
 * Handles configuration for QR code generation controls
 * - Data/content to encode
 * - Size and style options
 * - Error correction level
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';

interface QrCodeGeneratorConfig {
  value?: string;
  size?: number;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  darkColor?: string;
  lightColor?: string;
  logoUrl?: string;
  logoSize?: number;
  enableDownload?: boolean;
  downloadFormat?: 'png' | 'jpeg' | 'svg';
  includeMargin?: boolean;
  margin?: number;
}

/**
 * Render function for QR code generator editor
 */
function renderQrCodeGeneratorEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config as QrCodeGeneratorConfig) || {};

  const value = config.value || '';
  const size = config.size ?? 200;
  const errorCorrectionLevel = config.errorCorrectionLevel ?? 'M';
  const darkColor = config.darkColor ?? '#000000';
  const lightColor = config.lightColor ?? '#FFFFFF';
  const logoSize = config.logoSize ?? 40;
  const downloadFormat = config.downloadFormat ?? 'png';
  const includeMargin = config.includeMargin ?? true;
  const margin = config.margin ?? 10;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">QR Code Generator Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Value/Content to Encode
          <textarea
            value={value}
            onChange={(e) => onConfigChange('value', e.target.value)}
            className="af-properties-tab__input"
            placeholder="Enter text, URL, or other data to encode"
            rows={3}
            style={{ marginTop: '6px' }}
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          QR Code Size (pixels)
          <input
            type="number"
            value={size}
            onChange={(e) => onConfigChange('size', Math.max(50, parseInt(e.target.value, 10) || 200))}
            className="af-properties-tab__input"
            min="50"
            step="10"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Error Correction Level
          <select
            value={errorCorrectionLevel}
            onChange={(e) => onConfigChange('errorCorrectionLevel', e.target.value)}
            className="af-properties-tab__select"
          >
            <option value="L">Low (7% recovery)</option>
            <option value="M">Medium (15% recovery)</option>
            <option value="Q">Quartile (25% recovery)</option>
            <option value="H">High (30% recovery)</option>
          </select>
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Dark Color
          <input
            type="color"
            value={darkColor}
            onChange={(e) => onConfigChange('darkColor', e.target.value)}
            className="af-properties-tab__color-input"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Light Color (background)
          <input
            type="color"
            value={lightColor}
            onChange={(e) => onConfigChange('lightColor', e.target.value)}
            className="af-properties-tab__color-input"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Logo URL (optional)
          <input
            type="text"
            value={config.logoUrl || ''}
            onChange={(e) => onConfigChange('logoUrl', e.target.value)}
            className="af-properties-tab__input"
            placeholder="e.g., /logo.png"
          />
        </label>
      </div>

      {config.logoUrl && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Logo Size (% of QR code)
            <input
              type="number"
              value={logoSize}
              onChange={(e) => onConfigChange('logoSize', Math.max(10, Math.min(50, parseInt(e.target.value, 10) || 40)))}
              className="af-properties-tab__input"
              min="10"
              max="50"
            />
          </label>
        </div>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={includeMargin}
            onChange={(e) => onConfigChange('includeMargin', e.target.checked)}
          />
          Include Margin
        </label>
      </div>

      {includeMargin && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Margin (pixels)
            <input
              type="number"
              value={margin}
              onChange={(e) => onConfigChange('margin', Math.max(0, parseInt(e.target.value, 10) || 10))}
              className="af-properties-tab__input"
              min="0"
              step="5"
            />
          </label>
        </div>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={config.enableDownload !== false}
            onChange={(e) => onConfigChange('enableDownload', e.target.checked)}
          />
          Enable Download Button
        </label>
      </div>

      {config.enableDownload && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Download Format
            <select
              value={downloadFormat}
              onChange={(e) => onConfigChange('downloadFormat', e.target.value)}
              className="af-properties-tab__select"
            >
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="svg">SVG</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * QrCodeGeneratorEditor - Handles QR code generator control configuration
 */
export const QrCodeGeneratorEditor: PropertyEditor = {
  type: 'qr-code-generator',
  label: 'QR Code Generator',
  description: 'Configure QR code generation with customizable appearance',
  render: renderQrCodeGeneratorEditor,
};
